import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Tenant, TenantRole, TenantState } from "src/types/tenant.types";

const initialState: TenantState = {
  createTenant: {
    loading: false,
    data: null,
    error: null,
  },
  editTenant: {
    loading: false,
    data: null,
    error: null,
  },
  tenantList: {
    loading: false,
    data: null,
    error: null,
  },
  tenantDetails: {
    loading: false,
    data: null,
    error: null,
  },

  createTenantRole: {
    loading: false,
    data: null,
    error: null,
  },
  editTenantRole: {
    loading: false,
    data: null,
    error: null,
  },
  tenantRoleList: {
    loading: false,
    data: null,
    error: null,
  },
  deleteTenantRole: {
    loading: false,
    data: null,
    error: null,
  },
};

const tenantSlice = createSlice({
  name: "tenants",
  initialState,
  reducers: {
    createTenantRequest: (state, action: PayloadAction<Tenant>) => {
      state.createTenant.loading = true;
      state.createTenant.data = null;
      state.createTenant.error = null;
    },
    createTenantSuccess: (state, action: PayloadAction<any>) => {
      state.createTenant.loading = false;
      state.createTenant.data = action.payload;
      state.createTenant.error = null;
    },
    createTenantError: (state, action: PayloadAction<string>) => {
      state.createTenant.loading = false;
      state.createTenant.data = null;
      state.createTenant.error = action.payload;
    },
    resetCreateTenant: (state) => {
      state.createTenant.loading = false;
      state.createTenant.data = null;
      state.createTenant.error = null;
    },

    // edit tenant actions
    editTenantRequest: (state, action: PayloadAction<Tenant>) => {
      state.editTenant.loading = true;
      state.editTenant.data = null;
      state.editTenant.error = null;
    },
    editTenantSuccess: (state, action: PayloadAction<any>) => {
      state.editTenant.loading = false;
      state.editTenant.data = action.payload;
      state.editTenant.error = null;
    },
    editTenantError: (state, action: PayloadAction<string>) => {
      state.editTenant.loading = false;
      state.editTenant.data = null;
      state.editTenant.error = action.payload;
    },
    resetEditTenant: (state) => {
      state.editTenant.loading = false;
      state.editTenant.data = null;
      state.editTenant.error = null;
    },

    // get tenants list actions
    getTenantsListRequest: (
      state,
      action: PayloadAction<{
        page: number;
        limit: number;
        search?: string;
        status?: string;
      }>
    ) => {
      state.tenantList.loading = true;
      state.tenantList.data = null;
      state.tenantList.error = null;
    },
    getTenantsListSuccess: (state, action: PayloadAction<any>) => {
      state.tenantList.loading = false;
      state.tenantList.data = action.payload;
      state.tenantList.error = null;
    },
    getTenantsListError: (state, action: PayloadAction<string>) => {
      state.tenantList.loading = false;
      state.tenantList.data = null;
      state.tenantList.error = action.payload;
    },

    // get tenant details actions
    getTenantDetailsRequest: (state, action: PayloadAction<string>) => {
      state.tenantDetails.loading = true;
      state.tenantDetails.data = null;
      state.tenantDetails.error = null;
    },
    getTenantDetailsSuccess: (state, action: PayloadAction<any>) => {
      state.tenantDetails.loading = false;
      state.tenantDetails.data = action.payload?.data || null;
      state.tenantDetails.error = null;
    },
    getTenantDetailsError: (state, action: PayloadAction<string>) => {
      state.tenantDetails.loading = false;
      state.tenantDetails.data = null;
      state.tenantDetails.error = action.payload;
    },
    resetTenantDetails: (state) => {
      state.tenantDetails.loading = false;
      state.tenantDetails.data = null;
      state.tenantDetails.error = null;
    },

    createTenantRoleRequest: (
      state,
      action: PayloadAction<TenantRole & { isAdminAuth?: boolean }>
    ) => {
      state.createTenantRole.loading = true;
      state.createTenantRole.data = null;
      state.createTenantRole.error = null;
    },
    createTenantRoleSuccess: (state, action: PayloadAction<any>) => {
      state.createTenantRole.loading = false;
      state.createTenantRole.data = action.payload;
      state.createTenantRole.error = null;
    },
    createTenantRoleError: (state, action: PayloadAction<string>) => {
      state.createTenantRole.loading = false;
      state.createTenantRole.data = null;
      state.createTenantRole.error = action.payload;
    },
    resetCreateTenantRole: (state) => {
      state.createTenantRole.loading = false;
      state.createTenantRole.data = null;
      state.createTenantRole.error = null;
    },

    // edit tenant actions
    editTenantRoleRequest: (
      state,
      action: PayloadAction<{
        id: string;
        data: TenantRole;
        isAdminAuth?: boolean;
      }>
    ) => {
      state.editTenantRole.loading = true;
      state.editTenantRole.data = null;
      state.editTenantRole.error = null;
    },
    editTenantRoleSuccess: (state, action: PayloadAction<any>) => {
      state.editTenantRole.loading = false;
      state.editTenantRole.data = action.payload;
      state.editTenantRole.error = null;
    },
    editTenantRoleError: (state, action: PayloadAction<string>) => {
      state.editTenantRole.loading = false;
      state.editTenantRole.data = null;
      state.editTenantRole.error = action.payload;
    },
    resetEditTenantRole: (state) => {
      state.editTenantRole.loading = false;
      state.editTenantRole.data = null;
      state.editTenantRole.error = null;
    },

    // get tenants list actions
    getTenantRoleListRequest: (
      state,
      action: PayloadAction<{ tenantId?: string; isAdminAuth?: boolean }>
    ) => {
      state.tenantRoleList.loading = true;
      state.tenantRoleList.data = null;
      state.tenantRoleList.error = null;
    },
    getTenantRoleListSuccess: (state, action: PayloadAction<any>) => {
      state.tenantRoleList.loading = false;
      state.tenantRoleList.data = action.payload;
      state.tenantRoleList.error = null;
    },
    getTenantRoleListError: (state, action: PayloadAction<string>) => {
      state.tenantRoleList.loading = false;
      state.tenantRoleList.data = null;
      state.tenantRoleList.error = action.payload;
    },

    // delete tenants list actions
    deleteTenantRoleRequest: (
      state,
      action: PayloadAction<{ id: string; isAdminAuth?: boolean }>
    ) => {
      state.deleteTenantRole.loading = true;
      state.deleteTenantRole.data = null;
      state.deleteTenantRole.error = null;
    },
    deleteTenantRoleSuccess: (state, action: PayloadAction<any>) => {
      state.deleteTenantRole.loading = false;
      state.deleteTenantRole.data = action.payload;
      state.deleteTenantRole.error = null;
    },
    deleteTenantRoleError: (state, action: PayloadAction<string>) => {
      state.deleteTenantRole.loading = false;
      state.deleteTenantRole.data = null;
      state.deleteTenantRole.error = action.payload;
    },
    resetDeleteTenantRole: (state) => {
      state.editTenantRole.loading = false;
      state.editTenantRole.data = null;
      state.editTenantRole.error = null;
    },
  },
});

export const {
  // create tenant actions
  createTenantRequest,
  createTenantSuccess,
  createTenantError,
  resetCreateTenant,

  // edit tenant actions
  editTenantRequest,
  editTenantSuccess,
  editTenantError,
  resetEditTenant,

  // get tenants list actions
  getTenantsListRequest,
  getTenantsListSuccess,
  getTenantsListError,

  // get tenant details actions
  getTenantDetailsRequest,
  getTenantDetailsSuccess,
  getTenantDetailsError,
  resetTenantDetails,

  // create tenant role actions
  createTenantRoleRequest,
  createTenantRoleSuccess,
  createTenantRoleError,
  resetCreateTenantRole,

  // edit tenant role actions
  editTenantRoleRequest,
  editTenantRoleSuccess,
  editTenantRoleError,
  resetEditTenantRole,

  // get tenant role list actions
  getTenantRoleListRequest,
  getTenantRoleListSuccess,
  getTenantRoleListError,

  // delete tenant role actions
  deleteTenantRoleRequest,
  deleteTenantRoleSuccess,
  deleteTenantRoleError,
  resetDeleteTenantRole,
} = tenantSlice.actions;
export default tenantSlice.reducer;
